import React from "react";
import { Link } from "react-router-dom";
import { lastUpdated } from "../data/AppData";

const PrivacyPolicyPage = () => {
  const sections = [
    {
      id: "introduction",
      title: "1. Introduction & Scope",
      content: `At ContactHQ, your privacy is our primary responsibility. This policy describes how we handle personal data for our developers and business users across West Bengal, Rajasthan, Delhi, and the international community. By using our "No-Backend" form solutions and Centralized Inbox, you trust us with your information, and we are committed to maintaining that trust through total transparency.`,
    },
    {
      id: "data-collection",
      title: "2. Information We Collect",
      content: `We collect information to provide better services to all our users.
      
      (a) User-Provided Information: When you register for an account, we collect your name, email, and company details.
      (b) Form Submission Data: We process and store the data submitted by your end-users through ContactHQ form endpoints. This includes names, emails, and any custom fields you define.
      (c) Automatic Collection: We collect log files, device information, and IP addresses to prevent fraud and optimize our dashboard performance for different regions.`,
    },
    {
      id: "data-usage",
      title: "3. How We Use Information",
      content: `ContactHQ uses your data strictly for:
      
      - Delivering form submissions to your Centralized Inbox in real-time.
      - Generating Advanced Analytics to help you track submission trends.
      - Sending critical security alerts and account notifications.
      - Improving our Smart Form Generator and product experience based on anonymized usage patterns.
      
      We do not use your form submission data for advertising or profiling.`,
    },
    {
      id: "data-residency",
      title: "4. Data Residency & Protection",
      content: `For our users in India (including West Bengal, Rajasthan, and Delhi), we prioritize local data processing where required by the DPDP Act.
      
      (a) Encryption: All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption.
      (b) Security Audits: Our systems undergo regular vulnerability assessments to ensure your form endpoints remain secure from SQL injection and XSS attacks.`,
    },
    {
      id: "sharing",
      title: "5. Data Sharing & Third Parties",
      content: `We do not sell, trade, or rent your personal identification information to others. We may share information with:
      
      - Service Providers: Only trusted partners like cloud hosting providers (AWS/Google Cloud) and email delivery services (SendGrid) necessary for operations.
      - Legal Compliance: When required by Indian law or valid legal processes in the jurisdiction of Kolkata, West Bengal.`,
    },
    {
      id: "user-rights",
      title: "6. Your Rights & Control",
      content: `As per the latest data protection regulations, you have the right to:
      
      - Access & Export: Download all your form submissions in CSV/Excel format at any time.
      - Rectification: Correct any inaccurate account information via the dashboard.
      - Erasure (Right to be Forgotten): Delete your account and all associated form data permanently.
      - Consent Withdrawal: Opt-out of non-essential communications.`,
    },
    {
      id: "cookies",
      title: "7. Cookie Policy",
      content: `We use essential cookies to maintain your login session and remember your dashboard preferences. We do not use intrusive third-party tracking cookies. You can manage cookie preferences through your browser settings, though some features of ContactHQ may not function without them.`,
    },
    {
      id: "contact-privacy",
      title: "8. Contact Our Privacy Officer",
      content: `If you have questions about this Privacy Policy or our data practices, please contact our Data Protection Officer at our Kolkata headquarters. We aim to respond to all privacy-related inquiries within 48 hours.`,
    },
  ];

  return (
    <div className="bg-white min-h-screen selection:bg-[#E63946] selection:text-white overflow-x-hidden">
      <section className="bg-[#0B1C2D] pt-28 sm:pt-32 pb-14 sm:pb-20 lg:pb-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-10 left-10 size-64 bg-[#E63946] rounded-full blur-[100px]"></div>
        </div>

        <div className="mx-auto w-[95%] md:w-[90%] relative z-10 text-center lg:text-left">
          <Link
            to="/"
            className="inline-flex items-center text-sm font-bold text-[#E63946] mb-8 group animate-fade-in-up"
          >
            <span className="material-symbols-outlined text-sm mr-2 group-hover:-translate-x-1 transition-transform">
              arrow_back
            </span>
            Return to Homepage
          </Link>
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-tighter mb-4 sm:mb-6">
            Privacy <span className="text-[#E63946]">Policy.</span>
          </h1>
          <p className="max-w-2xl mx-auto lg:mx-0 text-base sm:text-lg text-slate-400 font-medium leading-relaxed">
            Your data, our responsibility. Learn how ContactHQ protects your
            information and respects your privacy under Indian and global laws.
          </p>
          <div className="mt-8 sm:mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-4 sm:gap-6">
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <span className="material-symbols-outlined text-[#E63946]">
                verified_user
              </span>
              DPDP Act Compliant
            </div>
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <span className="material-symbols-outlined text-[#E63946]">
                update
              </span>
              Updated: {lastUpdated}
            </div>
          </div>
        </div>
      </section>

      {/* DOCUMENT GRID */}
      <section className="py-12 sm:py-16 lg:py-24">
        <div className="mx-auto w-[95%] md:w-[90%]">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            {/* STICKY SIDEBAR */}
            <aside className="lg:w-1/4">
              <div className="lg:sticky lg:top-32 bg-slate-50 p-5 sm:p-6 rounded-2xl sm:rounded-[2rem] border border-[#E2E8F0] overflow-x-auto">
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-[#0B1C2D] mb-6 border-b border-[#E2E8F0] pb-4">
                  On this page
                </h3>
                <nav className="space-y-2">
                  {sections.map((sec) => (
                    <a
                      key={sec.id}
                      href={`#${sec.id}`}
                      className="block py-2 text-sm font-bold text-[#64748B] hover:text-[#E63946] transition-all"
                    >
                      {sec.title.split(".")[1]}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* MAIN CONTENT */}
            <main className="lg:w-3/4">
              <div className="space-y-12 sm:space-y-16 lg:space-y-20">
                {sections.map((sec) => (
                  <article key={sec.id} id={sec.id} className="scroll-mt-32">
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-black text-[#0B1C2D] mb-5 sm:mb-8 flex flex-wrap items-baseline gap-3 sm:gap-4">
                      <span className="text-sm font-black text-[#E63946] py-1 px-3 bg-red-50 rounded-lg">
                        {sec.title.split(".")[0]}
                      </span>
                      {sec.title.split(".")[1]}
                    </h2>
                    <div className="prose prose-slate max-w-none">
                      <p className="text-[#64748B] text-base sm:text-lg leading-7 sm:leading-9 whitespace-pre-line">
                        {sec.content}
                      </p>
                    </div>
                  </article>
                ))}
              </div>

              {/* HELP CARD */}
              <div className="mt-12 sm:mt-16 lg:mt-24 p-6 sm:p-8 lg:p-12 rounded-2xl sm:rounded-[3rem] bg-slate-50 border border-[#E2E8F0] flex flex-col md:flex-row items-center justify-between gap-6 sm:gap-8 text-center md:text-left">
                <div>
                  <h3 className="text-xl sm:text-2xl font-black text-[#0B1C2D] mb-2">
                    Need to request your data?
                  </h3>
                  <p className="text-[#64748B] font-medium">
                    Contact our support team for a full GDPR/DPDP data export.
                  </p>
                </div>
                <Link
                  to={"/contact"}
                  className="w-full md:w-auto px-6 sm:px-8 py-3.5 sm:py-4 bg-[#0B1C2D] text-white font-bold rounded-xl sm:rounded-2xl hover:bg-[#E63946] transition-all shadow-xl shadow-[#0B1C2D]/10 text-center whitespace-nowrap"
                >
                  Contact Privacy Team
                </Link>
              </div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
